import { useEffect, useState } from 'react';

const CONSENT_KEY = 'app_cookiesConsentInfo';
const CONSENT_DURATION_MS = 365 * 24 * 60 * 60 * 1000;

export interface CookieConsentPrefs {
  allowAnalyticsCookies: boolean;
}

interface StoredConsent extends CookieConsentPrefs {
  lastUpdated: number;
}

export interface CookieConsentInfo extends CookieConsentPrefs {
  lastUpdated: number;
  expired: boolean;
}

export function getCookieConsentInfo(): CookieConsentInfo | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const stored: StoredConsent = JSON.parse(raw);
    return { ...stored, expired: Date.now() - stored.lastUpdated >= CONSENT_DURATION_MS };
  } catch {
    return null;
  }
}

export function isAnalyticsCookiesEnabled(): boolean {
  const info = getCookieConsentInfo();
  return info !== null && !info.expired && info.allowAnalyticsCookies;
}

export function setCookieConsentInfo(prefs: CookieConsentPrefs): void {
  const stored: StoredConsent = { ...prefs, lastUpdated: Date.now() };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(stored));
}

export function shouldShowCookieConsentModal(): boolean {
  if (new URLSearchParams(window.location.search).get('__ignoreCookieConsent') === 'true') {
    return false;
  }
  const info = getCookieConsentInfo();
  return info === null || info.expired;
}

export function useCookieConsent() {
  const [visible, setVisible] = useState(false);
  const [closable, setClosable] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('__ignoreCookieConsent') === 'true') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('showCookieConsent') === '1') {
      setClosable(true);
      setVisible(true);
      params.delete('showCookieConsent');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    } else if (shouldShowCookieConsentModal()) {
      setClosable(false);
      setVisible(true);
    }
  }, []);

  function close() {
    setVisible(false);
  }

  return { visible, closable, close };
}
